import { cn, Stack } from 'component-library';
import { FC, HTMLAttributes, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { PlotReportFactLabel, PlotReportFactLabelNameEnum, ValueClass } from '@/api/rest/resources/types/fact';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
// TODO: MVP-5260 - Bring Confidence Interval back to charts
// import { RenderConfidenceInterval } from './ConfidenceInterval';
// import { useConfidenceIntervalLegendItem } from './hooks/useConfidenceIntervalLegendItem';
import { usePlotGroupFilterEndYear } from '@/hooks/usePlotGroupFilterEndYear';
import { usePlotGroupFilterStartYear } from '@/hooks/usePlotGroupFilterStartYear';
import { Logger } from '@/lib/logs/logger';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { isNullish } from '@/utils/booleans';
import { formatUnit } from '@/utils/formatting';
import { printMonthYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { BenchmarkPill } from '../Pill/BenchmarkPill';
import { ValueClassPill } from '../Pill/ValueClassPill';
import { Area, Chart, ComposedChart, getXAxisProps, TooltipProps } from './components/Chart';
import { CHART_ZOOM } from './components/ChartZoom';
import { CustomEstimationDot } from './components/CustomEstimationDot';
import { ChartLegendBar } from './components/LegendBar';
import {
  ChartTooltip,
  ChartTooltipDot,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { useEstimatedDataLegendItem } from './hooks/useEstimatedDataLegendItem';
import { useYAxisDomainFromMouseWheelZoom } from './hooks/useYAxisDomainFromMouseWheelZoom';
import { NoChartData } from './NoChartData';
import { extrapolateReferenceLinesChartData, RenderReferenceLine } from './ReferenceLines';
import { ChartProps } from './types';
import {
  applyBenchmarksToChartData,
  getBenchmarkLabel,
  getChartDataFromGraphFact,
  getDataDefaultDomain,
  getXAxisDomainForHistoricGraph,
  hasInterpolatedValues,
  intrapolatedValues,
  kgToTonneFormatter,
  sortChartData,
} from './utils';
import { getChartZoomedDomain } from './utils/getChartZoomedDomain';

const carbonColors = getColorForIndicator(INDICATOR.ABOVE_GROUND_CARBON);

const chartColors = {
  carbon: carbonColors,
  confidenceInterval: {
    fill: carbonColors.confidenceInterval,
  },
  estimatedData: { stroke: carbonColors.estimatedData },
} as const;

const totalFactNames = {
  cecil: {
    above_ground_carbon_total: 'cag',
    above_ground_carbon_total_benchmark_low: 'cag_benchmark_low',
    above_ground_carbon_total_benchmark_moderate: 'cag_benchmark_moderate',
    above_ground_carbon_total_benchmark_high: 'cag_benchmark_high',
  },
  esa: {
    esa_above_ground_carbon_total: 'cag',
    esa_above_ground_carbon_total_benchmark_low: 'cag_benchmark_low',
    esa_above_ground_carbon_total_benchmark_moderate: 'cag_benchmark_moderate',
    esa_above_ground_carbon_total_benchmark_high: 'cag_benchmark_high',
  },
};

const perHaFactNames = {
  cecil: {
    above_ground_carbon_per_ha: 'cag',
    above_ground_carbon_per_ha_benchmark_low: 'cag_benchmark_low',
    above_ground_carbon_per_ha_benchmark_moderate: 'cag_benchmark_moderate',
    above_ground_carbon_per_ha_benchmark_high: 'cag_benchmark_high',
  },
  esa: {
    esa_above_ground_carbon_per_ha: 'cag',
    esa_above_ground_carbon_per_ha_benchmark_low: 'cag_benchmark_low',
    esa_above_ground_carbon_per_ha_benchmark_moderate: 'cag_benchmark_moderate',
    esa_above_ground_carbon_per_ha_benchmark_high: 'cag_benchmark_high',
  },
};

export const AboveGroundCarbonStorageHistorical: FC<
  HTMLAttributes<HTMLDivElement> &
    ChartProps & {
      customBenchmark?: CustomBenchmarkSuccessResponse | null;
    }
> = ({ analysisType, data, benchmarks, className, showTooltip, zoom, setZoom, customBenchmark, ...delegated }) => {
  const { t } = useTranslation();
  const filtersStartYear = usePlotGroupFilterStartYear();
  const filtersEndYear = usePlotGroupFilterEndYear();

  const hasEsaData = data.every((fact) =>
    [...Object.keys(totalFactNames.esa), ...Object.keys(perHaFactNames.esa)].includes(fact.name),
  );

  const limitMaxYear = hasEsaData ?? filtersEndYear;

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const defaultDomain = getDataDefaultDomain(data, 0.8, 1.2, kgToTonneFormatter);
  const yAxisDomainFromMouseWheel = useYAxisDomainFromMouseWheelZoom(chartContainerRef, defaultDomain, setZoom);

  const styles = cn('flex w-full flex-col', className);
  const unit = analysisType === 'total' ? formatUnit(UnitEnum.t) : formatUnit(UnitEnum['t/ha']);

  const factNames = analysisType === 'total' ? totalFactNames : perHaFactNames;
  const factNamesBySource = hasEsaData ? factNames.esa : factNames.cecil;
  const dateDataMap = getChartDataFromGraphFact(data, factNamesBySource, kgToTonneFormatter);

  const sortedChartData = sortChartData(dateDataMap, (date) => new Date(date).getTime());
  const interpolatedData = intrapolatedValues(sortedChartData, 'cag');

  const firstTick = sortedChartData.at(0);
  const lastTick = sortedChartData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render chart');
    return (
      <div className={cn(styles, 'h-full justify-center')} {...delegated}>
        <NoChartData />
      </div>
    );
  }
  const xTicksDomain = getXAxisDomainForHistoricGraph(firstTick.label, lastTick.label, filtersStartYear, limitMaxYear);
  const xAxisProps = getXAxisProps(xTicksDomain);

  const chartData = applyBenchmarksToChartData(
    interpolatedData,
    customBenchmark,
    xTicksDomain,
    { low: 'cag_benchmark_low', moderate: 'cag_benchmark_moderate', high: 'cag_benchmark_high' },
    extrapolateReferenceLinesChartData,
  );

  const hasInterpolated = hasInterpolatedValues(chartData, 'cag');

  const hasLowData = chartData.some((point) => !!point.cag_benchmark_low);
  const hasModerateData = chartData.some((point) => !!point.cag_benchmark_moderate);
  const hasHighData = chartData.some((point) => !!point.cag_benchmark_high);

  const yAxisDomainFromButtons = getChartZoomedDomain({
    data,
    zoom,
    defaultDomain: undefined,
    formatter: kgToTonneFormatter,
  });

  const yAxisDomain = zoom === CHART_ZOOM.MOUSE_WHEEL ? yAxisDomainFromMouseWheel : yAxisDomainFromButtons;

  return (
    <>
      <Chart.Container
        ref={chartContainerRef}
        {...delegated}
        data={chartData}
        config={{
          cag: {
            label: t('shared.ncaDetail.details.aboveGroundCarbon.labels.aboveGroundCarbonStorage'),
            color: chartColors.carbon.stroke,
          },
        }}
      >
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id='cag' x1='0' y1='-0.1' x2='0' y2='1'>
              <stop offset='0%' stopColor={chartColors.carbon.stroke} stopOpacity={1} />
              <stop offset='100%' stopColor={chartColors.carbon.fill} stopOpacity={0} />
            </linearGradient>
          </defs>
          {/* TODO: MVP-5260 - Bring Confidence Interval back to charts */}
          {/* {RenderConfidenceInterval({
            dataKey: 'cag.confidence_interval',
            type: 'monotone',
            fill: chartColors.confidenceInterval.fill,
            id: 'cag-confidence-interval',
          })} */}
          <Area
            type='monotone'
            stroke='var(--color-cag)'
            strokeWidth={3}
            fill='url(#cag)'
            fillOpacity={1}
            dataKey='cag.value'
            connectNulls
            activeDot={ChartTooltipDot}
            dot={(props) => <CustomEstimationDot {...props} color={chartColors.estimatedData.stroke} />}
            id='cag-area'
          />
          {RenderReferenceLine({
            dataKey: 'cag_benchmark_low.value',
            type: 'monotone',
            label: getBenchmarkLabel(hasLowData, t('global.benchmark.low'), customBenchmark?.name),
            chartData,
            id: 'cag-benchmark-low',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'cag_benchmark_moderate.value',
            type: 'monotone',
            label: getBenchmarkLabel(hasModerateData, t('global.benchmark.moderate'), customBenchmark?.name),
            chartData,
            id: 'cag-benchmark-moderate',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'cag_benchmark_high.value',
            type: 'monotone',
            label: getBenchmarkLabel(hasHighData, t('global.benchmark.high'), customBenchmark?.name),
            chartData,
            id: 'cag-benchmark-high',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          <Chart.XAxis {...xAxisProps} className='cag-x-axis' />
          <Chart.YAxis domain={yAxisDomain} className='cag-y-axis' allowDataOverflow={true}>
            <Chart.Label>{unit}</Chart.Label>
          </Chart.YAxis>
          {showTooltip && (
            <Chart.Tooltip
              content={(props) => <CustomTooltip {...props} unit={unit as UnitEnum} benchmarks={benchmarks} />}
            />
          )}
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar showEstimate={hasInterpolated} />
    </>
  );
};

const CustomLegendBar = ({ showEstimate }: { showEstimate: boolean }) => {
  const { t } = useTranslation();
  // TODO: MVP-5260 - Bring Confidence Interval back to charts
  // const confidenceIntervalLegendItem = useConfidenceIntervalLegendItem({
  //   confidenceIntervalColor: chartColors.confidenceInterval.fill,
  // });

  const estimatedDataLegendItem = useEstimatedDataLegendItem({
    estimatedDataColor: chartColors.estimatedData.stroke ?? '',
  });

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t('global.analysis.aboveGroundCarbon'),
      color: chartColors.carbon.fill,
    },
    ...(showEstimate ? estimatedDataLegendItem : []),
    // TODO: MVP-5260 - Bring Confidence Interval back to charts
    // ...confidenceIntervalLegendItem,
  ];

  return (
    <ChartLegendBar items={legendBarItems} className='mt-8 sm:ml-[28px]' data-testid='carbon-storage-ag-chart-legend' />
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  unit,
  benchmarks,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
TooltipProps<ValueType, NameType> & { unit: UnitEnum; benchmarks?: any[] }) => {
  const { t } = useTranslation();

  const cagValue = payload?.find((data) => data.dataKey === 'cag.value');
  const cagDisplay = !isNullish(cagValue?.value)
    ? `${getDisplayNumber(cagValue?.value as string, window.navigator.language)} ${unit}`
    : t('global.analysis.noData');
  const cagBenchmarkType = useBenchmarkForFact(benchmarks, cagValue?.value);

  const dataPointHasLabel = cagValue && cagValue?.payload?.cag?.labels?.length > 0;
  const dataPointValueClass = dataPointHasLabel
    ? cagValue.payload.cag.labels.find(
        (dataLabel: PlotReportFactLabel) => dataLabel.name === PlotReportFactLabelNameEnum.value_class,
      )
    : undefined;
  const dataLabel = dataPointValueClass?.value as ValueClass;
  if (!active) return null;

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printMonthYear(label)}</ChartTooltipTextSecondary>
        {dataLabel && <ValueClassPill title={dataLabel} />}
      </Stack>
      {cagBenchmarkType && (
        <ChartTooltipTextSecondary>
          <BenchmarkPill benchmarkType={cagBenchmarkType} />
        </ChartTooltipTextSecondary>
      )}
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextPrimary>
          {`${t('shared.ncaDetail.details.aboveGroundCarbon.labels.aboveGroundCarbon')}`}
        </ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{cagDisplay}</ChartTooltipTextPrimary>
      </Stack>
      {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
      {/* <ChartTooltipConfidenceInterval ciPct={cagValue?.payload?.cag?.confidence_interval_pct} /> */}
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipAboveGroundCarbonStorageHistorical };
